import { OpenAPI, type TemplateQualityCheck } from '@beeboard/common';
import { getLocale } from '@/utils/i18n/vueI18n';
import type { TemplateQualityFinding } from '@beeboard/common';

// SSE consumer for the AI half of the quality check. The generated SDK buffers
// the whole response before resolving, which defeats streaming, so this mirrors
// `useAiStream`: raw fetch, reading the body and parsing SSE frames from a
// rolling buffer.

export interface TemplateQualityStreamCallbacks {
  onStatus?: (status: { total: number; processed: number }) => void;
  onFinding?: (finding: TemplateQualityFinding) => void;
  onDone?: (data: {
    processed: number;
    findings: TemplateQualityFinding[];
  }) => void;
  onError?: (message: string) => void;
}

interface SSEEvent {
  event: string;
  data: unknown;
}

// Parses whole SSE frames from `buffer`, returning the trailing partial frame
// to prepend to the next chunk. Frames are separated by a blank line.
function parseSSEBuffer(buffer: string): {
  parsed: SSEEvent[];
  remaining: string;
} {
  const events: SSEEvent[] = [];
  const lastSeparator = buffer.lastIndexOf('\n\n');
  if (lastSeparator === -1) {
    return { parsed: events, remaining: buffer };
  }

  const complete = buffer.slice(0, lastSeparator);
  const remaining = buffer.slice(lastSeparator + 2);

  for (const frame of complete.split('\n\n')) {
    let event = '';
    let data = '';
    for (const line of frame.split('\n')) {
      if (line.startsWith('event: ')) {
        event = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        data += line.slice(6); // multi-line data concatenates per the spec
      }
    }
    if (!event && !data) {
      continue;
    }
    try {
      events.push({ event, data: JSON.parse(data) });
    } catch {
      events.push({ event, data });
    }
  }

  return { parsed: events, remaining };
}

export function useTemplateQualityStream() {
  const isStreaming = ref(false);
  const error = ref<string | null>(null);
  const abortController = ref<AbortController | null>(null);

  async function startStream(
    templateId: string,
    options: { checks?: TemplateQualityCheck[] } = {},
    callbacks: TemplateQualityStreamCallbacks = {},
  ): Promise<{ processed: number; findings: TemplateQualityFinding[] }> {
    isStreaming.value = true;
    error.value = null;
    abortController.value = new AbortController();

    const collected: TemplateQualityFinding[] = [];

    // Every rejection below is preceded by an `onError` call (aborts resolve).
    try {
      let token = '';
      if (typeof OpenAPI.TOKEN === 'function') {
        token = await OpenAPI.TOKEN({} as never);
      } else if (OpenAPI.TOKEN) {
        token = OpenAPI.TOKEN;
      }

      const response = await fetch(
        `${OpenAPI.BASE}/v1/templates/${templateId}/quality-check/stream`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            language: getLocale(),
            ...(options.checks ? { checks: options.checks } : {}),
          }),
          signal: abortController.value.signal,
          credentials: OpenAPI.WITH_CREDENTIALS ? 'include' : 'same-origin',
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Quality check request failed: ${errorText}`);
      }
      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // finally cancels the reader — the `done` case returns out of the loop.
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const { parsed, remaining } = parseSSEBuffer(buffer);
          buffer = remaining;

          for (const sseEvent of parsed) {
            switch (sseEvent.event) {
              case 'status': {
                callbacks.onStatus?.(
                  sseEvent.data as { total: number; processed: number },
                );
                break;
              }

              case 'finding': {
                const { finding } = sseEvent.data as {
                  finding: TemplateQualityFinding;
                };
                if (finding) {
                  collected.push(finding);
                  callbacks.onFinding?.(finding);
                }
                break;
              }

              case 'done': {
                // Carries only counts; `collected` is the full finding set.
                const data = sseEvent.data as {
                  processed: number;
                  finding_count: number;
                };
                const result = {
                  processed: data.processed ?? collected.length,
                  findings: collected,
                };
                callbacks.onDone?.(result);
                isStreaming.value = false;

                return result;
              }

              case 'error': {
                const { message } = sseEvent.data as { message: string };
                throw new Error(message); // reported via the shared catch below
              }
            }
          }
        }
      } finally {
        try {
          await reader.cancel();
        } catch {
          // Already closed/errored — nothing to release.
        }
      }

      // Stream ended without a `done` frame — return what arrived.
      isStreaming.value = false;
      const result = { processed: collected.length, findings: collected };
      callbacks.onDone?.(result);

      return result;
    } catch (err) {
      isStreaming.value = false;

      // An abort is a user action, not a failure.
      if (err instanceof DOMException && err.name === 'AbortError') {
        return { processed: collected.length, findings: collected };
      }

      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred';
      error.value = errorMessage;
      callbacks.onError?.(errorMessage);
      throw err;
    }
  }

  function cancelStream() {
    if (abortController.value) {
      abortController.value.abort();
      abortController.value = null;
    }
    isStreaming.value = false;
  }

  return {
    isStreaming,
    error,
    startStream,
    cancelStream,
  };
}
